import { EventStatus, Language } from "@finbackoffice/enums";
import { CLIENT_DISCONNECTED } from "./constants/events";
import {
  ClientEventHandler,
  ClientSubscriptionParams,
  ClientsService,
  IMessagesFromServer,
  IMessagesToClient,
  SocketService,
} from "./services";
import { FeedStrategy as Feed, HandlingStrategyFactory, SystemStrategy as System } from "./strategies";
import {
  ICategoryFeedResponse,
  IEventFeedResponse,
  ISearchEventOptions,
  ISearchOptions,
  ITournamentFeedResponse,
  mergeMarkets,
} from "./utils";
import { IFeedResponseMarket } from "utils/feed.server.types";

export class WebsocketClient {
  private client: ClientsService;
  private socket: SocketService;
  private handlingStrategyFactory: HandlingStrategyFactory;
  private feed: Feed;
  private system: System;

  constructor(
    private url: string,
    private language: Language = Language.en,
    private shouldPing: boolean = true,
    private pingInterval: number = 1000,
  ) {
    this.client = new ClientsService();
    this.socket = new SocketService({
      url: this.url,
      shouldPing: this.shouldPing,
      onOpen: () => {
        this.system.sendConnectSystemMessage(this.language);
      },
      onClose: () => {
        this.feed.clear();
        this.client.sendClientMessage(CLIENT_DISCONNECTED, undefined);
      },
      onMessageError: (message: IMessagesFromServer) => {
        if (message.event === "error") {
          this.client.sendClientMessage(message.rid, undefined, message.data);
        }
      },
      onMessage: this.handleServerMessages,
    });
    this.handlingStrategyFactory = new HandlingStrategyFactory(this.client, this.socket);
    this.feed = this.handlingStrategyFactory.getFeedStrategy();
    this.system = this.handlingStrategyFactory.getSystemStrategy();
  }

  public destroy() {
    this.feed.clear();
    this.client.removeAllListeners();
    this.socket.destroy();
  }

  public on<T extends IMessagesToClient>(
    event: T,
    handler: ClientEventHandler<T>,
    params?: ClientSubscriptionParams<T>,
    statuses_to_delete?: EventStatus[],
  ): string {
    const strategy = this.handlingStrategyFactory.getStrategyByClientName(event);
    return (strategy.on as any)(event, handler, params, statuses_to_delete);
  }

  public off<T extends IMessagesToClient>(event: T, handler: ClientEventHandler<T>): string | undefined {
    const strategy = this.handlingStrategyFactory.getStrategyByClientName(event);
    return strategy.off(event, handler);
  }

  private handleServerMessages = (message: IMessagesFromServer) => {
    const strategies = this.handlingStrategyFactory.getStrategiesByServerName(message.event);
    for (const strategy of strategies) {
      strategy.handleServerMessage(message);
    }
  };

  public getSports(rid: string, options?: ISearchOptions) {
    return this.feed.getSports(rid, options);
  }

  public getCategories(rid: string, sportId: number, options?: ISearchOptions): ICategoryFeedResponse[] {
    return this.feed.getCategories(rid, sportId, options);
  }

  public getTournaments(rid: string, categoryId: number, options?: ISearchOptions): ITournamentFeedResponse[] {
    return this.feed.getTournaments(rid, categoryId, options);
  }

  public getSport(rid: string, id: number) {
    return this.feed.getSport(rid, id);
  }

  public getCategory(rid: string, id: number) {
    return this.feed.getCategory(rid, id);
  }

  public getTournament(rid: string, id: number) {
    return this.feed.getTournament(rid, id);
  }

  public getEvents(rid: string, tournamentId: number, options?: ISearchEventOptions): IEventFeedResponse[] {
    return this.feed.getEvents(rid, tournamentId, options);
  }

  public getEvent(rid: string, id: number) {
    return this.feed.getEvent(rid, id);
  }

  public getEventsBySportId(rid: string, id: number): IEventFeedResponse[] {
    return this.feed.getEventsBySportId(rid, id);
  }

  public getTopLeagues(rid: string) {
    return this.feed.getTopLeagues(rid);
  }

  public mergeEventMarkets(
    marketsMergeInto: IFeedResponseMarket[],
    marketsMergeFrom: IFeedResponseMarket[],
  ): IFeedResponseMarket[] {
    return mergeMarkets(marketsMergeInto, marketsMergeFrom);
  }
}
